/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { toTitleCase } from '../../../shared/functions';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Logger, LogService } from '@epg/angular-base-lib';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { WorkflowCreationModel, WorkflowModel } from '../../../../models/workflow.model';
import { WorkflowService } from '../../../../services/workflow.service';

const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;

@Component({
  selector: 'app-workflow-creation',
  templateUrl: './workflow-creation.component.html',
  styleUrls: ['./workflow-creation.component.scss'],
  standalone: false
})
export class WorkflowCreationComponent implements OnInit, OnDestroy {
  @Output() workflowData = new EventEmitter<{ name: string; description: string }>();
  @Output() cancel = new EventEmitter<void>();
  @Input() title: string | undefined;
  @Input() workflowToDuplicateId!: string;
  @Input() duplicatedWorkflowName!: string;
  @Input() duplicatedWorkflowDescription!: string;

  faXmark = faXmark;
  detailsForm!: FormGroup;
  allWorkflows: WorkflowModel[] = [];
  private timeoutId: any;
  private log: Logger;
  isCreating = false;
  duplicatedWorkflow: WorkflowModel | undefined;
  cancelButtonText: string = toTitleCase(cancelButtonText);
  constructor(
    private workflowService: WorkflowService,
    private logService: LogService,
    private cdr: ChangeDetectorRef,
    @Optional() private dialogRef: MatDialogRef<WorkflowCreationComponent>
  ) {
    this.log = logService.getLogger(this);
    this.initializeForm();
  }

  async ngOnInit() {
    await this.loadExistingWorkflows();
    if (this.workflowToDuplicateId) {
      this.fillForm();
    }
  }
  fillForm() {
    this.detailsForm.controls.name.setValue(this.duplicatedWorkflowName ?? '');
    this.detailsForm.controls.description.setValue(this.duplicatedWorkflowDescription ?? '');
  }

  ngOnDestroy(): void {
    this.detailsForm.reset();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  private initializeForm() {
    this.detailsForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      description: new FormControl('')
    });

    this.detailsForm.valueChanges.subscribe(() => {
      if (this.nameControl?.value) {
        this.validateName();
      }
    });
  }

  private async loadExistingWorkflows() {
    try {
      this.allWorkflows = await this.workflowService.getAllWorkflowsForValidation();
    } catch (error) {
      this.log.error('Error loading existing workflows:', error);
    }
  }

  onSubmit() {
    this.validateName();
    if (this.detailsForm.valid) {
      this.emitWorkflowData();
    }
  }

  validateAndEmitData() {
    this.validateName();
    if (this.detailsForm.valid) {
      this.emitWorkflowData();
      return true;
    }
    return false;
  }

  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.nameControl?.setValue(input.value.toLowerCase(), { emitEvent: false });
    this.validateName();
  }

  validateName() {
    const nameValue = this.nameControl?.value;

    if (!nameValue?.trim()) {
      this.setNameControlErrors({ required: true });
      return;
    }

    if (this.containsInvalidCharacters(nameValue)) {
      this.setNameControlErrors({ invalidCharacters: true });
      return;
    }

    this.debounceValidation(() => this.checkNameUniqueness(nameValue));
  }

  setNameControlErrors(errors: any) {
    this.nameControl?.setErrors(errors);
    this.nameControl?.markAsTouched();
    this.nameControl?.markAsDirty();
    this.cdr.detectChanges();
  }

  containsInvalidCharacters(name: string): boolean {
    const invalidCharsRegex = /[.\[\]\+*?^$(){}|\\=&\/@:%<>]/;
    return invalidCharsRegex.test(name);
  }

  private debounceValidation(callback: () => void) {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(callback, 50);
  }

  async checkNameUniqueness(nameValue: string) {
    const nameExists = this.allWorkflows.some(
      (workflow) => workflow.name.toLowerCase() === nameValue
    );

    if (nameExists) {
      this.setNameControlErrors({ repeat: true });
    } else {
      this.nameControl?.setErrors(null);
    }
    this.cdr.detectChanges();
  }

  emitWorkflowData() {
    this.workflowData.emit({
      name: this.detailsForm.value.name,
      description: this.detailsForm.value.description
    });
  }

  get nameControl() {
    return this.detailsForm.get('name');
  }

  get descriptionControl() {
    return this.detailsForm.get('description');
  }

  cancelClicked() {
    this.cancel.emit();
  }

  async createWorkflowInDesigner(): Promise<void> {
    this.validateName();
    await new Promise((resolve) => setTimeout(resolve, 100));

    if (!this.detailsForm.valid || this.isCreating) {
      return;
    }

    this.isCreating = true;

    try {
      if (this.workflowToDuplicateId) {
        await this.duplicateWorkflow();
      } else {
        await this.createNewWorkflow();
      }
    } catch (error) {
      this.log.error('Error creating workflow in designer:', error);
    } finally {
      this.isCreating = false;
    }
  }

  async duplicateWorkflow() {
    if (this.workflowToDuplicateId) {
      this.duplicatedWorkflow = await this.workflowService.duplicateWorkflow(
        this.workflowToDuplicateId,
        this.detailsForm.controls.name.value,
        this.detailsForm.controls.description.value
      );
      await this.openWorkflowDesigner(this.duplicatedWorkflow.designerId ?? '');
    }
  }
  async createNewWorkflow(): Promise<void> {
    const workflowData = new WorkflowCreationModel(
      this.detailsForm.value.name,
      this.detailsForm.value.description,
      '',
      [],
      undefined,
      undefined,
      true,
      false,
      false,
      0
    );

    const createdWorkflow = await this.createWorkflowInDesignerAPI(workflowData);

    if (createdWorkflow) {
      this.log.info('Workflow created successfully in designer:', createdWorkflow);
      await this.openWorkflowDesigner(createdWorkflow._id);
    }
  }

  private async createWorkflowInDesignerAPI(workflowData: WorkflowCreationModel): Promise<any> {
    return await this.workflowService.createWorkflowInDesigner(workflowData);
  }

  async openWorkflowDesigner(workflowId: string): Promise<void> {
    const combinedId = `_${workflowId}`;
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.cancel.emit();
    this.workflowService.navigateToDesigner(combinedId);
  }
}
